import { Event as EventModel } from '../../../model/Event'

/**
 * Shared model of "how far along is this event's schedule setup".
 *
 * Deliberately free of React and of any UI dependency so it can be unit
 * tested on its own. The wizard, the setup checklist and the contextual
 * empty-state alerts all derive their copy and their links from here, so
 * there is exactly one description of the setup order in the app.
 *
 * The order it encodes is the real dependency chain enforced by the API:
 *
 *   event dates  ->  locations + positions  ->  schedulable role
 *                ->  provisioned staff      ->  shifts & assignments
 */

export type SetupStepId =
  | 'basics'
  | 'locations'
  | 'positions'
  | 'roles'
  | 'staff'
  | 'shifts'

/** `unknown` is used when the caller didn't supply the data needed to decide. */
export type SetupStepStatus = 'complete' | 'incomplete' | 'unknown'

export type SetupStepAccess = 'SCHEDULE_ADMIN' | 'IDENTITY_MANAGER'

export interface SetupStepMeta {
  id: SetupStepId
  /** Short label for progress rails and checklist rows. */
  label: string
  /** One line shown under the label. */
  blurb: string
  /** Longer explanation shown inside the wizard step. */
  help: string
  /** What breaks if this step is skipped — used by the prerequisite alerts. */
  consequence: string
  /** Path segment under /event/:id/setup, or null for steps the wizard links out to. */
  wizardPath: string | null
  /** Access level needed to actually complete the step. */
  requiredAccess: SetupStepAccess
  /** Optional in the sense that shifts can still be created without it. */
  optional: boolean
  /** Steps that cannot be completed until this one is. */
  blocks: SetupStepId[]
  /** Whether the step counts toward the completion percentage. */
  countsTowardProgress: boolean
}

export interface SetupStepState extends SetupStepMeta {
  status: SetupStepStatus
  done: boolean
  /** Short status text, e.g. "3 added" or "None yet". */
  detail: string
  /** Non-blocking problems worth surfacing, e.g. a missing timezone. */
  warnings: string[]
}

export interface EventSetupStatus {
  steps: SetupStepState[]
  byId: Record<SetupStepId, SetupStepState>
  /** 0-100, over the steps that count toward progress and whose state is known. */
  percent: number
  completedCount: number
  requiredCount: number
  firstIncompleteStepId: SetupStepId | null
  /** Every prerequisite for creating a shift is in place. */
  canCreateShifts: boolean
  /** Every prerequisite for assigning someone to a shift spot is in place. */
  canAssignStaff: boolean
  isComplete: boolean
  /** Steps still missing before a shift can be created. */
  missingForShifts: SetupStepId[]
  /** Steps still missing before a spot can be filled. */
  missingForStaffing: SetupStepId[]
}

export interface EventSetupExtras {
  /** Length of GET /event/{id}/schedulable. Omit when unknown. */
  schedulableCount?: number
  /** Number of shifts on the event. Omit when unknown. */
  shiftCount?: number
}

export const SETUP_STEPS: SetupStepMeta[] = [
  {
    id: 'basics',
    label: 'Event Basics',
    blurb: 'Dates, timezone and first day of the week',
    help:
      'The event date range decides which days can be picked when you create a shift, ' +
      'and the first day of the week decides how the schedule grid is laid out. Get these ' +
      'right before you build any shifts — changing them later means reworking the schedule.',
    consequence: 'without a date range there are no days to schedule against',
    wizardPath: 'basics',
    requiredAccess: 'SCHEDULE_ADMIN',
    optional: false,
    blocks: ['locations', 'positions', 'roles', 'staff', 'shifts'],
    countsTowardProgress: true,
  },
  {
    id: 'locations',
    label: 'Locations',
    blurb: 'Physical venues where shifts take place',
    help:
      'Every shift is assigned to exactly one location. Add the venues, gates or areas ' +
      'people will be working at — you can add more at any time.',
    consequence: 'shifts cannot be created without at least one location',
    wizardPath: 'locations',
    requiredAccess: 'SCHEDULE_ADMIN',
    optional: false,
    blocks: ['shifts'],
    countsTowardProgress: true,
  },
  {
    id: 'positions',
    label: 'Positions',
    blurb: 'Job titles that can be assigned to shifts',
    help:
      'Every shift is assigned to exactly one position — the job being done, such as ' +
      'Gate Attendant or Supervisor. Positions are separate from roles: a position ' +
      'describes the work, a role describes the person.',
    consequence: 'shifts cannot be created without at least one position',
    wizardPath: 'positions',
    requiredAccess: 'SCHEDULE_ADMIN',
    optional: false,
    blocks: ['shifts'],
    countsTowardProgress: true,
  },
  {
    id: 'roles',
    label: 'Roles',
    blurb: 'At least one role marked as schedulable',
    help:
      'People are provisioned onto an event through a role. Only roles marked ' +
      '"Schedulable" produce staff who can be assigned to shift spots, so you need at ' +
      'least one of those before provisioning anybody.',
    consequence:
      'staff provisioned to a non-schedulable role will never appear on the schedule',
    wizardPath: 'roles',
    requiredAccess: 'SCHEDULE_ADMIN',
    optional: true,
    blocks: ['staff'],
    countsTowardProgress: true,
  },
  {
    id: 'staff',
    label: 'Staff',
    blurb: 'People provisioned into a schedulable role',
    help:
      'Quick Provisioning is where you put people onto this event. Pick a schedulable ' +
      'role for each person and they become available to drag onto shifts.',
    consequence: 'there is nobody available to assign to shift spots',
    wizardPath: 'staff',
    requiredAccess: 'IDENTITY_MANAGER',
    optional: false,
    blocks: [],
    countsTowardProgress: true,
  },
  {
    id: 'shifts',
    label: 'Shifts',
    blurb: 'Build the schedule',
    help:
      'With everything above in place you can create shifts and assign people to them ' +
      'on the schedule page.',
    consequence: '',
    wizardPath: null,
    requiredAccess: 'SCHEDULE_ADMIN',
    optional: false,
    blocks: [],
    countsTowardProgress: false,
  },
]

export const SETUP_STEPS_BY_ID: Record<SetupStepId, SetupStepMeta> =
  SETUP_STEPS.reduce(
    (acc, step) => {
      acc[step.id] = step
      return acc
    },
    {} as Record<SetupStepId, SetupStepMeta>
  )

/** Steps the wizard walks through, in order. Excludes the link-out `shifts` step. */
export const WIZARD_STEPS: SetupStepMeta[] = SETUP_STEPS.filter(
  (s) => s.wizardPath !== null
)

/** Where the checklist and the alerts send someone to complete a step. */
export function setupStepHref(id: SetupStepId, eventId: number | string): string {
  switch (id) {
    case 'basics':
      return `/event/${eventId}/config/info`
    case 'staff':
      return `/event/${eventId}/provision`
    case 'shifts':
      return `/event/${eventId}/schedule`
    default:
      return wizardHref(eventId, id)
  }
}

export function wizardHref(
  eventId: number | string,
  step?: SetupStepId | string
): string {
  return step
    ? `/event/${eventId}/setup/${step}`
    : `/event/${eventId}/setup`
}

const countDetail = (n: number, noun: string, plural?: string): string =>
  n === 0 ? 'None yet' : `${n} ${n === 1 ? noun : (plural ?? `${noun}s`)}`

export function computeEventSetupStatus(
  event: EventModel | null | undefined,
  extras: EventSetupExtras = {}
): EventSetupStatus {
  const locations = event?.locations?.length ?? 0
  const positions = event?.positions?.length ?? 0
  const roles = event?.roles ?? []
  const schedulableRoles = roles.filter((r) => r.schedulable).length

  const basicsWarnings: string[] = []
  if (event && !event.timezone && !event.client?.timezone) {
    basicsWarnings.push(
      'No timezone is set on this event or its client — shift times may not display as expected.'
    )
  }

  const resolve = (
    meta: SetupStepMeta,
    status: SetupStepStatus,
    detail: string,
    warnings: string[] = []
  ): SetupStepState => ({
    ...meta,
    status,
    done: status === 'complete',
    detail,
    warnings,
  })

  const known = (value: boolean): SetupStepStatus =>
    value ? 'complete' : 'incomplete'

  const steps: SetupStepState[] = SETUP_STEPS.map((meta) => {
    if (!event) return resolve(meta, 'unknown', 'Unknown')

    switch (meta.id) {
      case 'basics':
        return resolve(
          meta,
          known(!!event.startDate && !!event.endDate),
          event.startDate && event.endDate ? 'Dates set' : 'Dates missing',
          basicsWarnings
        )
      case 'locations':
        return resolve(meta, known(locations > 0), countDetail(locations, 'location'))
      case 'positions':
        return resolve(meta, known(positions > 0), countDetail(positions, 'position'))
      case 'roles': {
        const detail =
          schedulableRoles > 0
            ? `${schedulableRoles} schedulable`
            : roles.length > 0
              ? `${roles.length} role${roles.length === 1 ? '' : 's'}, none schedulable`
              : 'None yet'
        const warnings =
          roles.length > 0 && schedulableRoles === 0
            ? [
                'None of the roles on this event are marked schedulable, so nobody provisioned to them will appear on the schedule.',
              ]
            : []
        return resolve(meta, known(schedulableRoles > 0), detail, warnings)
      }
      case 'staff':
        return extras.schedulableCount === undefined
          ? resolve(meta, 'unknown', 'Unknown')
          : resolve(
              meta,
              known(extras.schedulableCount > 0),
              countDetail(extras.schedulableCount, 'person', 'people')
            )
      case 'shifts':
        return extras.shiftCount === undefined
          ? resolve(meta, 'unknown', 'Unknown')
          : resolve(
              meta,
              known(extras.shiftCount > 0),
              countDetail(extras.shiftCount, 'shift')
            )
      default:
        return resolve(meta, 'unknown', 'Unknown')
    }
  })

  const byId = steps.reduce(
    (acc, step) => {
      acc[step.id] = step
      return acc
    },
    {} as Record<SetupStepId, SetupStepState>
  )

  // Unknown steps are excluded from both sides of the fraction so a page that
  // didn't fetch the staff count doesn't render as permanently incomplete.
  const counted = steps.filter(
    (s) => s.countsTowardProgress && s.status !== 'unknown'
  )
  const completedCount = counted.filter((s) => s.done).length
  const requiredCount = counted.length
  const percent =
    requiredCount === 0 ? 0 : Math.round((completedCount / requiredCount) * 100)

  const firstIncomplete = steps.find(
    (s) => s.countsTowardProgress && s.status === 'incomplete'
  )

  const missingForShifts = (['basics', 'locations', 'positions'] as SetupStepId[]).filter(
    (id) => byId[id].status === 'incomplete'
  )
  const missingForStaffing = (['roles', 'staff'] as SetupStepId[]).filter(
    (id) => byId[id].status === 'incomplete'
  )

  return {
    steps,
    byId,
    percent,
    completedCount,
    requiredCount,
    firstIncompleteStepId: firstIncomplete ? firstIncomplete.id : null,
    canCreateShifts: missingForShifts.length === 0 && !!event,
    canAssignStaff:
      missingForShifts.length === 0 && missingForStaffing.length === 0 && !!event,
    isComplete: requiredCount > 0 && completedCount === requiredCount,
    missingForShifts,
    missingForStaffing,
  }
}
